import { signal, Signal } from '@preact/signals-react'
import { Subscription } from '../../core/Subscription'
import { StateDefinition, StateTypeMap } from './types'
import { JSONObject } from '../../types/common.types'
import { JSONPath } from 'jsonpath-plus'
import { Logger, LogLevel } from './Logger'

export enum StateChangeEventTypes {
    Change = 'change',
}

type StateChangeEventData = {
    key: string | undefined
    value: unknown
    type: 'set' | 'delete' | 'clear'
}

type StateChangeEventDataTypes = {
    [StateChangeEventTypes.Change]: StateChangeEventData
}

function validateStateType(
    key: string,
    value: unknown,
    expectedType: keyof StateTypeMap
): string | null {
    if (value === null || value === undefined) {
        return null
    }

    switch (expectedType) {
        case 'string':
            if (typeof value !== 'string') {
                return `State "${key}" expects type "string" but received "${typeof value}"${
                    Array.isArray(value) ? ' (array)' : ''
                }.`
            }
            break
        case 'number':
            if (typeof value !== 'number') {
                return `State "${key}" expects type "number" but received "${typeof value}"${
                    Array.isArray(value) ? ' (array)' : ''
                }.`
            }
            break
        case 'boolean':
            if (typeof value !== 'boolean') {
                return `State "${key}" expects type "boolean" but received "${typeof value}"${
                    Array.isArray(value) ? ' (array)' : ''
                }.`
            }
            break
        case 'array':
            if (!Array.isArray(value)) {
                return `State "${key}" expects type "array" but received "${typeof value}". This may cause components bound to this state to crash (e.g., calling .map() on a non-array).`
            }
            break
        case 'object':
            if (typeof value !== 'object' || Array.isArray(value)) {
                return `State "${key}" expects type "object" but received "${
                    Array.isArray(value) ? 'array' : typeof value
                }".`
            }
            break
    }

    return null
}

export class StateStore {
    private state: Map<string, Signal<unknown>> = new Map()
    private stateTypes: Map<string, keyof StateTypeMap> = new Map()
    private subscription: Subscription<StateChangeEventTypes, StateChangeEventDataTypes> =
        new Subscription()

    constructor(
        initialState: Record<string, unknown>,
        stateVariables?: StateDefinition[],
        inputData?: JSONObject
    ) {
        stateVariables?.forEach(sv => {
            this.stateTypes.set(sv.name, sv.type)

            try {
                if (sv.initialValue.type === 'value') {
                    this.state.set(sv.name, signal(sv.initialValue.value))
                } else if (sv.initialValue.type === 'dataBinding') {
                    const content = JSONPath({
                        path: sv.initialValue.value,
                        json: inputData || {},
                        wrap: false,
                    })
                    this.state.set(sv.name, signal(content))
                }
            } catch (error) {
                const logger = Logger.getInstance()
                logger.log(LogLevel.ERROR, `Failed to initialize state variable "${sv.name}"`, {
                    source: 'StateStore',
                    error: error instanceof Error ? error : String(error),
                    context: {
                        stateVariable: sv.name,
                        type: sv.type,
                        initialValueType: sv.initialValue.type,
                    },
                })
                this.state.set(sv.name, signal(null))
            }
        })

        Object.entries(initialState).forEach(([key, value]) => {
            this.state.set(key, signal(value))
        })
    }

    get(key: string): unknown {
        return this.state.get(key)?.value
    }

    getSignal(key: string): Signal<unknown> | undefined {
        return this.state.get(key)
    }

    set(key: string, value: unknown): void {
        const expectedType = this.stateTypes.get(key)
        if (expectedType) {
            const warning = validateStateType(key, value, expectedType)
            if (warning) {
                const logger = Logger.getInstance()
                logger.log(LogLevel.WARN, warning, {
                    source: 'StateStore',
                    context: {
                        key,
                        expectedType,
                        actualType: Array.isArray(value) ? 'array' : typeof value,
                        value:
                            typeof value === 'object'
                                ? JSON.stringify(value)?.substring(0, 100)
                                : String(value)?.substring(0, 100),
                    },
                })
            }
        }

        let sig = this.state.get(key)

        let newValue = value
        if (value && typeof value === 'object') {
            try {
                if (Array.isArray(value)) {
                    newValue = [...value]
                } else {
                    newValue = { ...value }
                }
            } catch (error) {
                const logger = Logger.getInstance()
                logger.log(
                    LogLevel.WARN,
                    `Failed to clone value for state "${key}", using original reference`,
                    {
                        source: 'StateStore',
                        error: error instanceof Error ? error : String(error),
                    }
                )
                newValue = value
            }
        }

        if (!sig) {
            sig = signal(newValue)
            this.state.set(key, sig)
        } else {
            sig.value = newValue
        }
        this.publishUpdate({ key, value, type: 'set' })
    }

    delete(key: string): void {
        this.state.delete(key)
        this.publishUpdate({ key, value: undefined, type: 'delete' })
    }

    clear(): void {
        this.state.clear()
        this.publishUpdate({ key: undefined, value: undefined, type: 'clear' })
    }

    toJSON(): Record<string, unknown> {
        const result: Record<string, unknown> = {}
        this.state.forEach((signal, key) => {
            result[key] = signal.value
        })
        return result
    }

    subscribe(callback: (data: StateChangeEventData) => void): string {
        return this.subscription.subscribe(StateChangeEventTypes.Change, callback)
    }

    unsubscribe(subscriptionId: string): void {
        this.subscription.unsubscribe(subscriptionId)
    }

    unsubscribeAll(): void {
        this.subscription.unsubscribeFromAll()
    }

    private publishUpdate(data: StateChangeEventData): void {
        this.subscription.publishUpdate(StateChangeEventTypes.Change, data)
    }
}
